import { config } from "@/shared/lib/config";
import { addMinutesToTimeHhMm, getTodayYYYYMMDD } from "@/shared/lib/date-time";
import { fetcher } from "@/shared/lib/fetcher";
import {
  PRIVATE_LESSON_DURATION_SELECT_OPTIONS,
  PRIVATE_LESSON_SLOT_DURATION_SET,
  type PrivateLessonSlotDurationMinutes,
} from "@/shared/lib/private-lesson-slot-duration";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  hydrateLookupItems,
} from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { FormInfiniteMultiCombobox } from "@/shared/ui/FormInfiniteMultiCombobox";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { QuarterHourMinuteSelect } from "@/shared/ui/QuarterHourMinuteSelect";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Button,
  FormDateField,
  FormInput,
  FormNumber,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useEffect, useMemo } from "react";
import { z } from "zod";
import type { BulkCreateClassSessionsDTO } from "../../domain/contracts/sessions";
import {
  useClassTemplateEligibleInstructorsQuery,
  useClassTemplateQuery,
} from "../api/queries";
import { instructorsFromLeadAssist } from "../lib/session-instructor-form";

type ClassTemplateItem = { id: string; name: string };
type RoomItem = { id: string; name: string };
type InstructorItem = { id: string; name: string };

const DAYS_OF_WEEK: { value: number; label: string }[] = [
  { value: 1, label: "ຈັນ" },
  { value: 2, label: "ອັງຄານ" },
  { value: 3, label: "ພຸດ" },
  { value: 4, label: "ພະທັດ" },
  { value: 5, label: "ສຸກ" },
  { value: 6, label: "ເສົາ" },
  { value: 7, label: "ອາທິດ" },
];

const slotDurZ = z.union([
  z.literal(15),
  z.literal(30),
  z.literal(45),
  z.literal(60),
  z.literal(75),
  z.literal(90),
  z.literal(105),
  z.literal(120),
  z.literal(135),
  z.literal(150),
  z.literal(165),
  z.literal(180),
]);

const DayRowSchema = z.object({
  dayOfWeek: z.number(),
  label: z.string(),
  enabled: z.boolean(),
  startTime: z.string(),
  slotDurationMinutes: slotDurZ,
  roomId: z.string(),
});

const locationKindFormZ = z.enum(["studio", "off_site"]);
const engagementKindFormZ = z.enum(["standard", "partner_instructor_only"]);

const FormSchema = z
  .object({
    templateId: z.string().min(1, "ຕ້ອງເລືອກແມ່ແບບຄລາດສ໌"),
    dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "ວັນທີບໍ່ຖືກຕ້ອງ"),
    dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "ວັນທີບໍ່ຖືກຕ້ອງ"),
    locationKind: locationKindFormZ,
    offSiteLabel: z.string().optional(),
    engagementKind: engagementKindFormZ,
    dayRows: z.array(DayRowSchema),
    leadInstructorId: z.string().min(1, "ຕ້ອງເລືອກຄູຫຼັກ"),
    assistInstructorIds: z.array(z.string()).max(3),
    capacity: z
      .number()
      .int("ຄວາມຈຸຕ້ອງເປັນຈຳນວນເຕັມ")
      .positive("ຄວາມຈຸຕ້ອງເປັນຈຳນວນບວກ"),
    notes: z.string().nullable().optional(),
  })
  .refine((d) => d.dateFrom <= d.dateTo, {
    message: "ວັນທີສິ້ນສຸດຕ້ອງບໍ່ໃຫ້ເກີນວັນທີເລີ່ມຕົ້ນ",
    path: ["dateTo"],
  })
  .refine((d) => d.dayRows.some((r) => r.enabled), {
    message: "ຕ້ອງເລືອກວັນຢ່າງໜ້ອຍ 1 ວັນ",
    path: ["dayRows"],
  })
  .refine(
    (d) => {
      for (const r of d.dayRows) {
        if (!r.enabled) continue;
        if (!r.startTime?.trim() || r.slotDurationMinutes == null) return false;
        if (d.locationKind === "studio" && !r.roomId?.trim()) return false;
        if (d.locationKind === "off_site" && r.roomId?.trim()) return false;
      }
      return true;
    },
    {
      message: "ວັນທີເປີດ: ສະຕູດິໂອຕ້ອງມີຫ້ອງ; ນອກສະຖານທີ່ບໍ່ກຳນົດຫ້ອງ",
      path: ["dayRows"],
    },
  )
  .refine(
    (d) => {
      const lead = d.leadInstructorId.trim();
      const assists = d.assistInstructorIds ?? [];
      return !assists.some((id) => id === lead);
    },
    {
      message: "ຄູຫຼັກກັບຄູຊ່ວຍຕ້ອງບໍ່຋້ຳກັນ",
      path: ["assistInstructorIds"],
    },
  )
  .refine(
    (d) =>
      !(
        d.engagementKind === "partner_instructor_only" &&
        (d.assistInstructorIds?.length ?? 0) > 0
      ),
    {
      message: "ຄລາດສ໌ Partner ບໍ່ສາມາດເພີ່ມຄູຊ່ວຍ (TA)",
      path: ["assistInstructorIds"],
    },
  );

type FormValues = z.infer<typeof FormSchema>;

type BulkCreateSessionsFormProps = {
  onSubmit: (values: BulkCreateClassSessionsDTO) => void;
  onCancel: () => void;
  submitting?: boolean;
};

const toHHmmss = (s: string) => (s.length === 5 ? `${s}:00` : s);

export function BulkCreateSessionsForm({
  onSubmit,
  onCancel,
  submitting,
}: BulkCreateSessionsFormProps) {
  const defaultDayRows = useMemo(
    () =>
      DAYS_OF_WEEK.map((d) => ({
        dayOfWeek: d.value,
        label: d.label,
        enabled: false,
        startTime: "09:00",
        slotDurationMinutes: 60 as PrivateLessonSlotDurationMinutes,
        roomId: "",
      })),
    [],
  );

  const methods = RHF.useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      templateId: "",
      dateFrom: getTodayYYYYMMDD(),
      dateTo: getTodayYYYYMMDD(),
      locationKind: "studio",
      offSiteLabel: "",
      engagementKind: "standard",
      dayRows: defaultDayRows,
      leadInstructorId: "",
      assistInstructorIds: [],
      capacity: 20,
      notes: null,
    },
  });

  const templateId = methods.watch("templateId");
  const dayRows = methods.watch("dayRows") ?? defaultDayRows;
  const locationKindWatch = methods.watch("locationKind");
  const engagementKind = methods.watch("engagementKind");
  const assistInstructorIds = methods.watch("assistInstructorIds") ?? [];

  useEffect(() => {
    if (
      engagementKind === "partner_instructor_only" &&
      assistInstructorIds.length > 0
    ) {
      methods.setValue("assistInstructorIds", []);
    }
  }, [engagementKind, assistInstructorIds.length, methods]);

  useEffect(() => {
    if (locationKindWatch !== "off_site") return;
    const rows = methods.getValues("dayRows");
    if (!rows?.some((r) => r.roomId?.trim())) return;
    methods.setValue(
      "dayRows",
      rows.map((r) => ({ ...r, roomId: "" })),
    );
  }, [locationKindWatch, methods]);

  const { data: template } = useClassTemplateQuery(templateId || "");
  const { data: eligibleInstructors } =
    useClassTemplateEligibleInstructorsQuery(templateId || "");

  const durationMinutes =
    template && typeof template.duration === "number" ? template.duration : 0;

  const rowEffectiveDurationMins = (
    row: (typeof defaultDayRows)[number],
  ): number =>
    durationMinutes > 0 ? durationMinutes : row.slotDurationMinutes;

  const rowEndDisplay = (row: (typeof defaultDayRows)[number]): string => {
    if (!row.startTime?.trim()) return "—";
    try {
      return addMinutesToTimeHhMm(
        row.startTime.trim(),
        rowEffectiveDurationMins(row),
      );
    } catch {
      return "—";
    }
  };

  const toggleDay = (index: number) => {
    const rows = [...(methods.getValues("dayRows") ?? defaultDayRows)];
    const row = rows[index];
    if (!row) return;
    rows[index] = {
      ...row,
      enabled: !row.enabled,
      startTime: row.startTime || "09:00",
      slotDurationMinutes: PRIVATE_LESSON_SLOT_DURATION_SET.has(
        row.slotDurationMinutes,
      )
        ? row.slotDurationMinutes
        : 60,
      roomId: row.roomId || "",
    };
    methods.setValue("dayRows", rows);
  };

  const updateDayRow = (
    index: number,
    field: keyof FormValues["dayRows"][0],
    value: unknown,
  ) => {
    const rows = [...(methods.getValues("dayRows") ?? defaultDayRows)];
    const row = rows[index];
    if (!row) return;
    rows[index] = { ...row, [field]: value };
    methods.setValue("dayRows", rows);
  };

  const handleSubmit = (vals: FormValues) => {
    const loc = vals.locationKind;
    const daySchedules = vals.dayRows
      .filter((r) => r.enabled)
      .map((r) => {
        const start = (r.startTime || "09:00").trim();
        const dur = rowEffectiveDurationMins(r);
        const base = {
          dayOfWeek: r.dayOfWeek,
          startTime: toHHmmss(start),
          endTime: toHHmmss(addMinutesToTimeHhMm(start, dur)),
        };
        if (loc === "studio") {
          return { ...base, roomId: r.roomId.trim() };
        }
        return base;
      });

    onSubmit({
      templateId: vals.templateId,
      dateFrom: vals.dateFrom,
      dateTo: vals.dateTo,
      locationKind: loc,
      offSiteLabel:
        loc === "off_site" ? vals.offSiteLabel?.trim() || null : null,
      engagementKind: vals.engagementKind,
      daySchedules,
      instructors: instructorsFromLeadAssist(
        vals.leadInstructorId,
        vals.engagementKind === "partner_instructor_only"
          ? []
          : (vals.assistInstructorIds ?? []),
      ),
      capacity: vals.capacity,
      notes: vals.notes ?? undefined,
    });
  };

  return (
    <FormRoot methods={methods} onSubmit={handleSubmit} className="space-y-6">
      <div className="rounded-xl border bg-card p-6">
        <h3 className="mb-4 flex items-center gap-2 font-semibold text-lg">
          <span className="size-2 rounded-full bg-red-600" />
          ແມ່ແບບ ແລະ ຊ່ວງວັນທີ
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormInfiniteCombobox<ClassTemplateItem>
            name="templateId"
            label="ເລືອກແມ່ແບບ"
            placeholder="ເລືອກແມ່ແບບທີ່ມີຢູ່..."
            requiredMark
            queryKey={["classes", "templates", "lookup"]}
            queryFn={async ({ search, pageParam }) => {
              const limit = 20;
              const skip = (pageParam - 1) * limit;
              const url = new URL(`${config.apiUrl}/classes/templates/lookup`);
              url.searchParams.set("limit", String(limit));
              url.searchParams.set("skip", String(skip));
              if (search) url.searchParams.set("q", search);
              const data = await fetcher.get<{
                items: Array<{
                  id: string;
                  name: string;
                  classTypeName: string;
                  levelName: string;
                }>;
                total: number;
              }>(url.toString());
              return {
                items: data.items.map((t) => ({
                  id: t.id,
                  name: t.name,
                })),
                nextPage: skip + limit < data.total ? pageParam + 1 : null,
              };
            }}
            preloadQueryFn={async (id) => {
              const t = await fetcher.get<{
                id: string;
                classTypeName: string;
                levelName: string;
              }>(`${config.apiUrl}/classes/templates/${id}`);
              return {
                id: t.id,
                name: `${t.classTypeName} - ${t.levelName}`,
              };
            }}
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
          />

          <FormDateField name="dateFrom" label="ຈາກວັນທີ" requiredMark />
          <FormDateField name="dateTo" label="ເຖິງວັນທີ" requiredMark />

          <div className="space-y-4 rounded-md border border-border bg-muted/20 p-4 md:col-span-2">
            <p className="text-muted-foreground text-xs leading-relaxed">
              ການສ້າງເປັນກຸ່ມໃຊ້ກົດດຽວກັບສ້າງທີລະ session: ສະຕູດິໂອຕ້ອງເລືອກຫ້ອງຕໍ່ວັນ;
              ນອກສະຖານທີ່ບໍ່ຜູກຫ້ອງ ແລະ ຍັງກວດຄູຊ້ຳເວລາ. ປະເພດ Partner ບໍ່ເປີດການເຊັກຊື່
              ນັກຮຽນແພັກເກດ (ຄືກັນກັບຟອມສ້າງດຽວ).
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <RHF.Controller
                name="locationKind"
                control={methods.control}
                render={({ field }) => (
                  <div className="space-y-2">
                    <div className="font-medium text-sm">ປະເພດສະຖານທີ່</div>
                    <SimpleSelect
                      value={field.value}
                      onValueChange={(v) =>
                        field.onChange(v as "studio" | "off_site")
                      }
                      options={[
                        { value: "studio", label: "ສະຕູດິໂອ" },
                        { value: "off_site", label: "ນອກສະຖານທີ່" },
                      ]}
                      className="h-9 w-full"
                    />
                  </div>
                )}
              />
              {locationKindWatch === "off_site" ? (
                <FormInput
                  name="offSiteLabel"
                  label="ຊື່ສະຖານທີ່ (ທາງເລືອກ)"
                  placeholder="ຕົວຢ່າງ: ໂຮງຮຽນ ມປລ"
                />
              ) : (
                <div className="hidden md:block" aria-hidden />
              )}
              <RHF.Controller
                name="engagementKind"
                control={methods.control}
                render={({ field }) => (
                  <div className="space-y-2 md:col-span-2">
                    <div className="font-medium text-sm">ປະເພດໂຄງການ</div>
                    <SimpleSelect
                      value={field.value}
                      onValueChange={(v) =>
                        field.onChange(
                          v as "standard" | "partner_instructor_only",
                        )
                      }
                      options={[
                        { value: "standard", label: "ມາດຕະຖານ" },
                        {
                          value: "partner_instructor_only",
                          label: "Partner (instructor-only)",
                        },
                      ]}
                      className="h-9 w-full"
                    />
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border bg-card p-6">
        <h3 className="mb-4 flex items-center gap-2 font-semibold text-lg">
          <span className="size-2 rounded-full bg-red-600" />
          ວັນໃນອາທິດ ແລະ ເວລາ
          {locationKindWatch === "studio"
            ? " (ຫ້ອງຕໍ່ວັນ)"
            : " (ນອກສະຖານທີ່ — ບໍ່ມີຫ້ອງ)"}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b">
                <th className="pr-4 pb-2 text-left font-medium">ວັນ</th>
                <th className="pr-4 pb-2 text-left font-medium">ເລືອກ</th>
                <th className="pr-4 pb-2 text-left font-medium">ເວລາເລີ່ມ</th>
                <th className="pr-4 pb-2 text-left font-medium">
                  ຄວາມຍາວ (ນາທີ)
                </th>
                <th className="pr-4 pb-2 text-left font-medium">
                  ເວລາສິ້ນສຸດ{" "}
                  <span className="font-normal text-muted-foreground">
                    (ຄຳນວນ)
                  </span>
                </th>
                <th className="pb-2 text-left font-medium">
                  {locationKindWatch === "off_site" ? "ສະຖານທີ່" : "ຫ້ອງ"}
                </th>
              </tr>
            </thead>
            <tbody>
              {dayRows.map((row, index) => (
                <tr
                  key={row.dayOfWeek}
                  className={`border-b ${row.enabled ? "bg-muted/30" : ""}`}
                >
                  <td className="py-2 pr-4 font-medium">{row.label}</td>
                  <td className="py-2 pr-4">
                    <button
                      type="button"
                      onClick={() => toggleDay(index)}
                      className={`rounded-md border px-3 py-1.5 font-medium text-sm transition-colors ${row.enabled
                          ? "border-red-600 bg-red-600 text-white"
                          : "border-border bg-background text-muted-foreground hover:bg-muted"
                        }`}
                    >
                      {row.enabled ? "ເລືອກແລ້ວ" : "ເລືອກ"}
                    </button>
                  </td>
                  <td className="py-2 pr-4">
                    <QuarterHourMinuteSelect
                      value={row.startTime || "09:00"}
                      onChange={(next) =>
                        updateDayRow(index, "startTime", next)
                      }
                      disabled={!row.enabled}
                      className="max-w-[200px]"
                    />
                  </td>
                  <td className="py-2 pr-4 align-top">
                    {durationMinutes > 0 ? (
                      <div className="flex min-h-9 w-full min-w-[120px] max-w-[220px] items-center rounded-md border border-dashed bg-muted/50 px-3 text-muted-foreground text-sm">
                        {durationMinutes} ນາທີ (ແມ່ແບບ)
                      </div>
                    ) : (
                      <SimpleSelect
                        value={String(row.slotDurationMinutes)}
                        onValueChange={(val) => {
                          const m = Number(val);
                          if (!PRIVATE_LESSON_SLOT_DURATION_SET.has(m)) return;
                          updateDayRow(
                            index,
                            "slotDurationMinutes",
                            m as PrivateLessonSlotDurationMinutes,
                          );
                        }}
                        options={PRIVATE_LESSON_DURATION_SELECT_OPTIONS}
                        className="h-9 w-full min-w-[120px] max-w-[220px]"
                        disabled={!row.enabled}
                        placeholder="ເລືອກຄວາມຍາວ"
                      />
                    )}
                  </td>
                  <td className="py-2 pr-4 align-top">
                    <div className="flex min-h-9 w-full min-w-[72px] max-w-[140px] items-center rounded-md border border-input border-dashed bg-muted/50 px-3 text-muted-foreground text-sm">
                      {row.enabled ? rowEndDisplay(row) : "—"}
                    </div>
                  </td>
                  <td className="py-2">
                    {locationKindWatch === "off_site" ? (
                      <span className="text-muted-foreground text-sm">—</span>
                    ) : (
                      <DayRoomCombobox
                        value={row.roomId}
                        onChange={(v) => updateDayRow(index, "roomId", v)}
                        disabled={!row.enabled}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {methods.formState.errors.dayRows && (
          <p className="mt-2 text-destructive text-xs">
            {methods.formState.errors.dayRows.message}
          </p>
        )}
      </div>

      <div className="rounded-xl border bg-card p-6">
        <h3 className="mb-4 flex items-center gap-2 font-semibold text-lg">
          <span className="size-2 rounded-full bg-red-600" />
          ຄູສອນ ແລະ ຄວາມຈຸ (ໃຊ້ຮ່ວມທຸກວັນ)
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-4 md:col-span-2">
            <FormInfiniteCombobox<InstructorItem>
              name="leadInstructorId"
              label="ຄູຫຼັກ"
              placeholder={
                templateId && eligibleInstructors
                  ? "ເລືອກຄູຫຼັກທີ່ອະນຸຍາດ..."
                  : "ເລືອກຄູຫຼັກ"
              }
              requiredMark
              queryKey={
                templateId && eligibleInstructors
                  ? ["classes", "templates", "eligible-instructors", templateId, "lead"]
                  : ["master", "instructors", "lookup", "bulk-lead"]
              }
              queryFn={({ search, pageParam }) => {
                if (templateId && eligibleInstructors) {
                  const filtered = eligibleInstructors.filter((inst) =>
                    search
                      ? inst.name.toLowerCase().includes(search.toLowerCase())
                      : true,
                  );
                  const limit = 20;
                  const skip = (pageParam - 1) * limit;
                  const paginated = filtered.slice(skip, skip + limit);
                  return Promise.resolve({
                    items: paginated.map((inst) => ({
                      id: inst.id,
                      name: inst.name,
                    })),
                    nextPage:
                      skip + limit < filtered.length ? pageParam + 1 : null,
                  });
                }
                return fetchLookupForInfinite<InstructorItem>(
                  `${config.apiUrl}/master/instructors/lookup`,
                  { search, pageParam },
                );
              }}
              preloadQueryFn={(id) => {
                if (templateId && eligibleInstructors) {
                  const inst = eligibleInstructors.find((i) => i.id === id);
                  return Promise.resolve(
                    inst ? { id: inst.id, name: inst.name } : null,
                  );
                }
                return hydrateLookupItem<InstructorItem>(
                  `${config.apiUrl}/master/instructors/lookup`,
                  id,
                );
              }}
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              clearable
            />
            {engagementKind !== "partner_instructor_only" ? (
              <div className="space-y-1">
                <FormInfiniteMultiCombobox<InstructorItem>
                  name="assistInstructorIds"
                  label="ຄູຊ່ວຍ (TA)"
                  placeholder="ເລືອກຄູຊ່ວຍ ສູງສຸດ 3 ຄົນ"
                  queryKey={
                    templateId && eligibleInstructors
                      ? [
                        "classes",
                        "templates",
                        "eligible-instructors",
                        templateId,
                        "assist",
                      ]
                      : ["master", "instructors", "lookup", "bulk-assist"]
                  }
                  queryFn={({ search, pageParam }) => {
                    if (templateId && eligibleInstructors) {
                      const filtered = eligibleInstructors.filter((inst) =>
                        search
                          ? inst.name
                            .toLowerCase()
                            .includes(search.toLowerCase())
                          : true,
                      );
                      const limit = 20;
                      const skip = (pageParam - 1) * limit;
                      const paginated = filtered.slice(skip, skip + limit);
                      return Promise.resolve({
                        items: paginated.map((inst) => ({
                          id: inst.id,
                          name: inst.name,
                        })),
                        nextPage:
                          skip + limit < filtered.length
                            ? pageParam + 1
                            : null,
                      });
                    }
                    return fetchLookupForInfinite<InstructorItem>(
                      `${config.apiUrl}/master/instructors/lookup`,
                      { search, pageParam },
                    );
                  }}
                  preloadQueryFn={(ids) => {
                    if (templateId && eligibleInstructors) {
                      return Promise.resolve(
                        ids
                          .map((id) => {
                            const inst = eligibleInstructors.find(
                              (i) => i.id === id,
                            );
                            return inst
                              ? { id: inst.id, name: inst.name }
                              : null;
                          })
                          .filter(
                            (item): item is InstructorItem => item !== null,
                          ),
                      );
                    }
                    return hydrateLookupItems<InstructorItem>(
                      `${config.apiUrl}/master/instructors/lookup`,
                      ids,
                    );
                  }}
                  getLabel={(item) => item.name}
                  getValue={(item) => item.id}
                />
                <p className="text-muted-foreground text-xs">
                  ຄິດຄ່າຈ້າງຕໍ່ຄັ້ງ · ສູງສຸດ 3 ຄົນ · ບໍ່຋້ຳກັບຄູຫຼັກ
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                ຄລາດສ໌ Partner ໃຊ້ຄູຫຼັກຄົນດຽວ — ບໍ່ມີຄູຊ່ວຍ (TA)
              </p>
            )}
          </div>
          <FormNumber
            name="capacity"
            label="ຈຳນວນນັກຮຽນ (Capacity)"
            placeholder="ປ້ອນຄວາມຈຸ"
            requiredMark
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={submitting}
        >
          ຍົກເລີກ
        </Button>
        <Button
          type="submit"
          className="bg-red-600 hover:bg-red-700"
          disabled={submitting}
        >
          ສ້າງຊົ່ວໂມງຮຽນ
        </Button>
      </div>
    </FormRoot>
  );
}

function DayRoomCombobox({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className={disabled ? "pointer-events-none opacity-50" : ""}>
      <InfiniteCombobox<RoomItem>
        value={value}
        onValueChange={onChange}
        placeholder="ເລືອກຫ້ອງ"
        queryKey={["master", "rooms", "lookup"]}
        queryFn={({ search, pageParam }) =>
          fetchLookupForInfinite<RoomItem>(
            `${config.apiUrl}/master/rooms/lookup`,
            { search, pageParam },
          )
        }
        preloadQueryFn={(id) =>
          hydrateLookupItem<RoomItem>(
            `${config.apiUrl}/master/rooms/lookup`,
            id,
          ).then((item) => item ?? null)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        className="min-w-[140px]"
      />
    </div>
  );
}
